import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  ArrowRight,
  Building2,
  ChevronRight,
  ClipboardCheck,
  FileCheck2,
  FileText,
  ListPlus,
  Plus,
  TrendingUp,
  Users,
  Workflow,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CHART_COLORS, chartAxisTick, chartTooltipProps } from "@/components/partner/chartTheme";
import { usePartnerDashboard } from "@/hooks/usePartnerDashboard";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/formatters";

export const Route = createFileRoute("/partner/dashboard/")({ component: PartnerDashboard });

const QUICK_ACTIONS = [
  {
    title: "Create slot",
    subtitle: "Open a new placement opportunity",
    icon: <Plus />,
    to: "/partner/slots/new",
  },
  {
    title: "Review logs",
    subtitle: "Approve or return submissions",
    icon: <ClipboardCheck />,
    to: "/partner/work-logs",
  },
  {
    title: "Partner profile",
    subtitle: "Update company information",
    icon: <FileText />,
    to: "/partner/profile",
  },
];

function PartnerDashboard() {
  const { data: dashboard, isLoading } = usePartnerDashboard();

  if (isLoading) {
    return <PageSkeleton />;
  }

  const kpis = dashboard?.kpis || {
    total_slots: 0,
    active_placements: 0,
    pending_reviews: 0,
  };
  const pendingLogs = dashboard?.pending_logs || [];
  const activityData = dashboard?.activity_trend || [];
  const slotData = dashboard?.slot_data || [];
  const availableSlots = Math.max((kpis.total_slots || 0) - (kpis.active_placements || 0), 0);
  const fillRate =
    kpis.total_slots > 0 ? Math.round((kpis.active_placements / kpis.total_slots) * 100) : 0;

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="Partner workspace"
        subtitle="Manage placement slots, supervise students, and review submitted work logs."
        action={
          <>
            <Button variant="outline" asChild>
              <Link to={"/partner/students" as any}>
                <Users />
                View students
              </Link>
            </Button>
            <Button asChild>
              <Link to={"/partner/slots/new" as any}>
                <Plus />
                Create slot
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
            <StatCard
              label="Active students"
              value={kpis.active_placements}
              hint="Currently placed"
              icon={<Users />}
            />
            <StatCard
              label="Available slots"
              value={availableSlots}
              hint={`${fillRate}% capacity filled`}
              icon={<Workflow />}
            />
            <StatCard
              label="Pending logs"
              value={kpis.pending_reviews}
              hint="Awaiting review"
              icon={<ClipboardCheck />}
            />
            <StatCard
              label="Total slots"
              value={kpis.total_slots}
              hint="Placement capacity"
              icon={<Building2 />}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
            <SectionCard
              title="Student activity trend"
              description="Daily submitted work logs from assigned students"
              actions={
                <Badge variant="success">
                  <TrendingUp />
                  Live logs
                </Badge>
              }
            >
              <div className="h-[260px] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={activityData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={chartAxisTick}
                      dy={8}
                    />
                    <YAxis axisLine={false} tickLine={false} tick={chartAxisTick} width={36} />
                    <RechartsTooltip cursor={{ fill: "var(--muted)" }} {...chartTooltipProps} />
                    <Bar dataKey="logs" fill="var(--chart-1)" radius={[8, 8, 0, 0]} barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard title="Slot fill rate" description="Overall placement capacity">
              {slotData.length > 0 ? (
                <div className="flex flex-col items-center">
                  <div className="h-[180px] w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={slotData}
                          cx="50%"
                          cy="50%"
                          innerRadius={56}
                          outerRadius={78}
                          paddingAngle={3}
                          dataKey="value"
                          stroke="var(--card)"
                        >
                          {slotData.map((entry: any, index: number) => (
                            <Cell
                              key={index}
                              fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip {...chartTooltipProps} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-[28px] leading-9 font-normal tracking-[-0.8px] text-foreground tabular-nums">
                    {fillRate}%
                  </p>
                  <p className="text-caption text-muted-foreground">Capacity filled</p>
                </div>
              ) : (
                <EmptyState
                  bare
                  icon={<FileCheck2 />}
                  title="No slot data yet"
                  description="No slot data available yet"
                  className="py-8"
                />
              )}
            </SectionCard>
          </div>

          <SectionCard
            title="Pending student reviews"
            description="Work logs awaiting partner approval"
            noPadding
            actions={
              <Button variant="ghost" size="sm" asChild className="max-sm:hidden">
                <Link to={"/partner/work-logs" as any}>
                  View all
                  <ArrowRight />
                </Link>
              </Button>
            }
          >
            {pendingLogs.length > 0 ? (
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Student</TableHead>
                    <TableHead>Activity date</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingLogs.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="size-8 rounded-full">
                            <AvatarFallback className="rounded-full text-xs">
                              {log.studentName?.charAt(0) || "S"}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-foreground">
                            {log.studentName || "Student"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(log.date)}</TableCell>
                      <TableCell className="font-mono tabular-nums">{log.hours}h</TableCell>
                      <TableCell>
                        <StatusChip status={log.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" asChild>
                          <Link to={`/partner/work-logs/${log.id}` as any}>Review</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyState
                bare
                icon={<ListPlus />}
                title="All caught up"
                description="No pending reviews at the moment"
              />
            )}
          </SectionCard>
        </div>

        <aside className="flex min-w-0 flex-col gap-4">
          <SectionCard title="Review pulse">
            <div className="space-y-5">
              <div className="rounded-sm border border-border bg-muted/40 p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Activity className="size-4 text-muted-foreground" aria-hidden />
                    Pending workload
                  </span>
                  <span
                    className={cn(
                      "font-mono text-sm tabular-nums",
                      kpis.pending_reviews > 0 ? "text-warning" : "text-success",
                    )}
                  >
                    {kpis.pending_reviews}
                  </span>
                </div>
                <p className="text-caption leading-5 text-muted-foreground">
                  Review submitted work logs to keep student progress current and verified.
                </p>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Slot utilization</span>
                  <span className="font-mono text-sm text-body tabular-nums">{fillRate}%</span>
                </div>
                <Progress value={fillRate} aria-label="Slot utilization" />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Quick actions" noPadding>
            <ul className="divide-y divide-border">
              {QUICK_ACTIONS.map((action) => (
                <li key={action.title}>
                  <Link
                    to={action.to as any}
                    className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/60 md:px-5"
                  >
                    <span
                      className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
                      aria-hidden
                    >
                      {action.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">
                        {action.title}
                      </span>
                      <span className="block truncate text-caption text-muted-foreground">
                        {action.subtitle}
                      </span>
                    </span>
                    <ChevronRight
                      className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}
